<mat-toolbar>
  <h1 class="page-title">Gym Profile Completeness</h1>
  @if (loading() || exporting()) {
    <mat-spinner [diameter]="30"></mat-spinner>
  }
</mat-toolbar>

<mat-card class="gymfinder-card">
  <div class="filters">
    <mat-form-field class="form-column search-field">
      <mat-label>Search by name or ID</mat-label>
      <input
        id="searchInput"
        name="searchInput"
        aria-label="Search by name or ID"
        #search
        matInput
        placeholder="Search by name or ID"
        (input)="searchValue.set(search.value)"
        (keydown.enter)="applySearch(search.value)"
        (blur)="applySearch(search.value)" />
        @if (searchValue()) {
          <button
            matSuffix
            mat-icon-button
            type="button"
            aria-label="Clear search"
            (click)="search.value = ''; clearSearch()">
            <mat-icon>close</mat-icon>
          </button>
        }
    </mat-form-field>
    <mat-form-field class="form-column">
      <mat-label>Network</mat-label>
      <mat-select
        multiple
        [value]="filters().networks"
        (selectionChange)="applyNetworks($event.value)">
        @for (option of networkFilterOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.display }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    <button
      mat-raised-button
      color="primary"
      type="button"
      class="export-button"
      [disabled]="loading() || exporting() || total() === 0"
      (click)="exportCsv()">
      <mat-icon>download</mat-icon>
      Export as CSV
    </button>
  </div>

  @if (error(); as httpError) {
    <gymfinder-alert variant="error" class="summary-alert">
      <span style="text-align: center">
        <strong>Could not load gyms.</strong>
        <p>Status: {{ httpError.status }}</p>
      </span>
    </gymfinder-alert>
  }

  @if (summaryVisible() && summary(); as stats) {
    <gymfinder-alert variant="warning" class="summary-alert">
      <div class="summary-content">
        <div class="summary-header">
          <strong
            >Incomplete gyms by category &mdash; {{ summaryScope() }}</strong
          >
        </div>

        <div class="summary-note">
          {{ stats.total | number }} gyms are incomplete in at least one
          category. A gym can be counted in several categories, so these numbers
          add up to more than the total. Counts cover {{ summaryScope() }} and
          are not affected by the search or sorting.
        </div>

        <div class="summary-stats-row">
          @for (category of categories; track category.key) {
            <div class="stat-item">
              <span class="stat-label">{{ category.label }}:</span>
              <span class="stat-value">{{ stats[category.key] | number }}</span>
            </div>
          }
        </div>
        <button
          mat-icon-button
          type="button"
          class="summary-dismiss"
          aria-label="Dismiss summary"
          (click)="dismissSummary()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </gymfinder-alert>
  }

  @if (showEmptyState()) {
    <div class="empty-wrapper">
      @if (hasSearchTerm()) {
        <span class="empty-title">No incomplete gyms match your search.</span>
      } @else {
        <span class="empty-title">No incomplete gyms found.</span>
        <span class="empty-subtitle">Amazing!</span>
      }
    </div>
  }

  <mat-card-content>
    <div class="table-container">
      @if (gyms().length) {
        <table mat-table [dataSource]="gyms()" class="completeness-table">
          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef>Location</th>
            <td mat-cell *matCellDef="let row">
              @let gym = asGym(row);
              @if (gym.gymName; as gymName) {
                <div class="location-name">{{ gymName }}</div>
              } @else {
                <div class="location-name value-unset">Unnamed gym</div>
              }
              <div class="location-id">{{ gym.egymLegacyGymId }}</div>
            </td>
          </ng-container>

          <ng-container matColumnDef="network">
            <th mat-header-cell *matHeaderCellDef>Network</th>
            <td mat-cell *matCellDef="let row">
              @let gym = asGym(row);
              @if (gym.network; as network) {
                {{ network }}
              } @else {
                <span class="value-unset">Not set</span>
              }
            </td>
          </ng-container>

          @for (category of categories; track category.key) {
            <ng-container [matColumnDef]="category.key">
              <th
                mat-header-cell
                *matHeaderCellDef
                [attr.aria-sort]="
                  filters().sorting === category.sort ? 'ascending' : 'none'
                ">
                <button
                  type="button"
                  class="sortable-header"
                  [class.active-sort]="filters().sorting === category.sort"
                  (click)="sortBy(category.sort)">
                  {{ category.label }}
                </button>
              </th>
              <td mat-cell *matCellDef="let row">
                @let status = statusDisplay[asGym(row)[category.statusKey]] ?? { label: '-', styleClass: 'value-unset' };
                <span class="status" [ngClass]="status.styleClass">
                  {{ status.label }}
                </span>
              </td>
            </ng-container>
          }

          <ng-container matColumnDef="profile">
            <th mat-header-cell *matHeaderCellDef>Profile</th>
            <td mat-cell *matCellDef="let row">
              <a
                class="view-profile-link"
                [routerLink]="[
                  '/gym-location/gymfinder/gym',
                  asGym(row).egymLegacyGymId,
                ]"
                target="_blank">
                View Profile
              </a>
            </td>
          </ng-container>

          <tr
            mat-header-row
            *matHeaderRowDef="displayedColumns"
            class="completeness-header"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="completeness-row"></tr>
        </table>
      }
    </div>

    <mat-paginator
      [ngClass]="[gyms().length ? 'completeness-paginator' : 'not-visible']"
      [pageSizeOptions]="pageSizeOptions"
      [length]="total()"
      [pageIndex]="pagination().page"
      [pageSize]="pagination().pageSize"
      (page)="onPage($event)" />
  </mat-card-content>
</mat-card>
